/**
 * UI-31: Action Queue — Decision-Force Interface
 *
 * The #1 action is THE decision. Everything else supports it.
 *
 * Layout:
 *   - Dominant first card (P1) — separated with stronger spacing
 *   - Secondary cards grouped by domain (P3) — compact, muted
 *   - Order stability preserved between refreshes (P6)
 *   - Empty state: "All systems stable" + reassurance (P7)
 */

import { useMemo, useRef } from 'react';
import { ActionCard } from './ActionCard';
import { computeActions } from '../../services/action-engine/actionEngine';
import type { ActionEngineInput, Action } from '../../services/action-engine/actionTypes';
import type {
  OperationalAlert,
  GuestExperienceRisk,
  PulseScoreCore,
  Situation,
  ActionTargetItem,
} from '../../services/controlTowerTypes';
import type { ActionResult } from '../../hooks/useActionExecution';

// ─── Constants ───────────────────────────────────────────────────────────────

const MAX_DISPLAY = 5;

const DOMAIN_GROUP_LABEL: Record<string, string> = {
  housekeeping: 'Housekeeping',
  operations: 'Operations',
  maintenance: 'Maintenance',
  staff: 'Staff capacity',
};

// ─── Order Stability (Principle 6 + M1 Hardening) ───────────────────────────

/**
 * Partial-match order stability:
 *   1. Retained actions keep their previous relative order
 *   2. New actions are appended in engine-sorted position
 *   3. Removed actions are simply omitted
 */
function stabilizeOrder(
  incoming: readonly Action[],
  previousIds: readonly string[],
): readonly Action[] {
  if (previousIds.length === 0) return incoming;

  const incomingById = new Map(incoming.map(a => [a.id, a]));

  // Retained actions in their previous relative order
  const retained: Action[] = [];
  for (const id of previousIds) {
    const action = incomingById.get(id);
    if (action) retained.push(action);
  }

  // New actions not in previous set (appended in engine-sorted order)
  const retainedIds = new Set(retained.map(a => a.id));
  const added = incoming.filter(a => !retainedIds.has(a.id));

  return [...retained, ...added];
}

// ─── Domain Labels (P3) ──────────────────────────────────────────────────────

/**
 * Computes inline domain separator labels for secondary actions.
 * Returns indices where a new domain starts (only when ≥2 consecutive
 * actions share a domain, or when multiple domains are present).
 *
 * CRITICAL: Does NOT reorder actions — engine ordering is sacred.
 * Labels are injected between actions, not used for grouping.
 */
function computeDomainLabels(
  actions: readonly Action[],
): Map<number, string> {
  const labels = new Map<number, string>();
  if (actions.length <= 1) return labels;

  // Count actions per domain to decide if labels are useful
  const domainCounts = new Map<string, number>();
  for (const a of actions) {
    domainCounts.set(a.domain, (domainCounts.get(a.domain) ?? 0) + 1);
  }

  // Only show labels if there are multiple domains
  if (domainCounts.size <= 1) return labels;

  let prevDomain: string | null = null;
  for (let i = 0; i < actions.length; i++) {
    const domain = actions[i]!.domain;
    if (domain !== prevDomain) {
      const count = domainCounts.get(domain) ?? 1;
      const label = DOMAIN_GROUP_LABEL[domain] ?? domain;
      labels.set(i, `${label}${count > 1 ? ` (${count})` : ''}`);
      prevDomain = domain;
    }
  }
  return labels;
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface ActionQueueProps {
  alerts: OperationalAlert[];
  risks: GuestExperienceRisk[];
  pulse: PulseScoreCore;
  hotelId: string;
  referenceTimeMs: number;
  /** Backend-derived situation (UI-3.1.5 patch) */
  situation?: Situation;
  /** PILOT-23: Top pending requests for action card specificity */
  topPendingItems?: readonly ActionTargetItem[];
  /** UI-32: Execute action with feedback loop */
  onExecuteAction: (action: Action) => void;
  /** UI-32: Check if action is currently being executed */
  isExecuting?: (actionId: string) => boolean;
  /** UI-32: Check if action completed recently */
  isCompleted?: (actionId: string) => boolean;
  /** UI-32: Get unified ActionResult for a completed action (P2) */
  getResult?: (actionId: string) => ActionResult | undefined;
  /** P8: IDs currently executing — preserved across polling recomputation */
  executionGuardIds?: ReadonlySet<string>;
  /** UI-43: Action ID of last no-op execution — suppress from list immediately */
  suppressedActionId?: string | null;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function ActionQueue({
  alerts,
  risks,
  pulse,
  hotelId,
  referenceTimeMs,
  situation,
  topPendingItems,
  onExecuteAction,
  isExecuting,
  isCompleted,
  getResult,
  executionGuardIds,
  suppressedActionId,
}: ActionQueueProps) {
  const previousIdsRef = useRef<readonly string[]>([]);
  /** Cache of last-known actions by ID — used to preserve executing actions (C1 fix) */
  const actionCacheRef = useRef<Map<string, Action>>(new Map());

  const actions = useMemo(() => {
    const input: ActionEngineInput = {
      alerts,
      risks,
      pressure: {
        pulseScore: pulse.score,
        dimensions: pulse.dimensions,
      },
      state: {
        hotelId,
        referenceTimeMs,
        pulse,
        alerts,
        guestRisks: risks,
      },
      situation,
      liveRequests: topPendingItems,
    };
    const result = computeActions(input);
    const raw = result.actions.slice(0, MAX_DISPLAY);

    // UI-3.1.5: In SURGE/CRITICAL_OVERLOAD, the engine returns exactly 1 action.
    // Skip the execution guard — situation supersedes all previous actions.
    const isSituationOverride = situation?.level === 'SURGE' || situation?.level === 'CRITICAL_OVERLOAD';

    // UI-43: Suppress action that was just executed and returned mutatedCount === 0
    const filtered = suppressedActionId
      ? raw.filter(a => a.id !== suppressedActionId)
      : raw;

    // C1 fix: preserve executing actions that polling may have removed
    // (only in NORMAL/BUILDING — situation overrides must not be diluted)
    const rawIds = new Set(filtered.map(a => a.id));
    const preserved: Action[] = [...filtered];
    if (executionGuardIds && !isSituationOverride) {
      for (const guardId of executionGuardIds) {
        if (!rawIds.has(guardId)) {
          const cached = actionCacheRef.current.get(guardId);
          if (cached) preserved.push(cached);
        }
      }
    }

    const stable = stabilizeOrder(preserved, previousIdsRef.current);
    previousIdsRef.current = stable.map(a => a.id);
    // Update action cache
    for (const a of stable) {
      actionCacheRef.current.set(a.id, a);
    }
    return stable;
  }, [alerts, risks, pulse, hotelId, referenceTimeMs, situation, topPendingItems, executionGuardIds, suppressedActionId]);

  const handleExecute = (action: Action) => {
    onExecuteAction(action);
  };

  // ─── Empty state ────────────────────────────────────────────────────────────

  if (actions.length === 0) {
    return (
      <div
        data-testid="action-queue"
        data-empty="true"
        className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-5 shadow-lg"
      >
        <div className="flex items-center gap-2 mb-3">
          <span className="text-sm">🚨</span>
          <h2 className="text-sm font-semibold text-slate-200">Action Required</h2>
        </div>
        <div className="flex items-center justify-center py-6">
          <div className="text-center">
            <span className="text-lg">✅</span>
            <p className="text-sm font-medium text-emerald-400 mt-1">All systems stable</p>
            <p className="text-[11px] text-slate-500 mt-0.5">No immediate action required</p>
          </div>
        </div>
      </div>
    );
  }

  // ─── Active actions ─────────────────────────────────────────────────────────

  const firstAction = actions[0]!;
  const secondaryActions = actions.slice(1);
  const domainLabels = computeDomainLabels(secondaryActions);
  const criticalCount = actions.filter((a) => a.priority === 'critical').length;

  return (
    <div
      data-testid="action-queue"
      className={`rounded-2xl border p-4 shadow-lg ${
        criticalCount > 0
          ? 'bg-rose-500/5 border-rose-500/20'
          : 'bg-slate-800/50 border-amber-500/20'
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm">🚨</span>
          <h2 className="text-sm font-semibold text-slate-200">Action Required</h2>
          <span className="text-[10px] text-slate-500">
            {actions.length} action{actions.length !== 1 ? 's' : ''}
          </span>
        </div>
        {criticalCount > 0 && (
          <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-400 border border-rose-500/30 animate-pulse">
            {criticalCount} critical
          </span>
        )}
      </div>

      {/* Dominant first action (P1) */}
      <ActionCard
        action={firstAction}
        isFirst={true}
        executing={isExecuting?.(firstAction.id)}
        completed={isCompleted?.(firstAction.id)}
        actionResult={getResult?.(firstAction.id)}
        onExecute={handleExecute}
      />

      {/* Secondary actions — engine order preserved, domain labels inline (P3) */}
      {secondaryActions.length > 0 && (
        <div className="mt-3 space-y-1" data-testid="secondary-actions">
          {secondaryActions.map((action, index) => (
            <div key={action.id}>
              {domainLabels.has(index) && (
                <p data-testid="domain-group-label" className="text-[9px] uppercase tracking-wider text-slate-600 font-medium mb-1 ml-1 mt-1.5 first:mt-0">
                  {domainLabels.get(index)}
                </p>
              )}
              <ActionCard
                action={action}
                executing={isExecuting?.(action.id)}
                completed={isCompleted?.(action.id)}
                actionResult={getResult?.(action.id)}
                onExecute={handleExecute}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
